import { globalTheme } from "../../../common/theme";
import axios from "axios";
import React, { useImperativeHandle, useMemo } from "react";
import { DataGridPro, GridColumns, GridRenderCellParams, GridRowId, GridSelectionModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import { CustomerAddressItem } from "./CustomerAccessories";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { AlertColor, Button, CircularProgress, IconButton, Modal, TextField, Typography, useMediaQuery } from "@mui/material";
import { Add, Cancel, CheckCircle, Delete, Save } from "@mui/icons-material";
import { renewToken } from "../../../services/AuthService";
import Title from "../../component/Title";
import { CustomTablePagination } from "../../component/CustomPagination";

export interface CustomerAddressHandle {
  Initialise: (customerId: number) => void;
}

export interface CustomerAddressProps {
    onClose: Function;
}

export const CustomerAddress = React.forwardRef(
    (props: CustomerAddressProps, ref: React.Ref<CustomerAddressHandle>) => {
      const style = { ...globalTheme() };

      const tinyScreen = useMediaQuery('(max-width:1366px)');
      const smallScreen = useMediaQuery('(max-width:1920px)');
      const normalScreen = useMediaQuery('(max-width:2560px)');
  
      const [open, setOpen] = React.useState(false);
      const [loading, setLoading] = React.useState<boolean>(false);
      const [buttonSelector, setButtonSelector] = React.useState<number>(1);
      const [customerId, setCustomerId] = React.useState<number>();

      const [gridData, setGridData] = React.useState<CustomerAddressItem[]>([]);
      const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>(() =>
        gridData.map((r) => r.id),
      );

      const selectedCustomerAddress = React.useRef<CustomerAddressItem>();
      const [customerAddress, setCustomerAddress] = React.useState<string>(""); 
      const [showAddress, setShowAddress] = React.useState<boolean>(false); 
      const [saveMode, setSaveMode] = React.useState<number>(0); 

      //Grid columns
      const columns: GridColumns = [
        {
          field: 'id', headerName: '', disableColumnMenu: true, sortable: false, 
          width: 60,
          renderCell: (cellValues) => {
            return (
              <IconButton
                onClick={(event) => {
                  DeleteRow(event, cellValues);
                }}
              >
                <Delete />
              </IconButton>
            );
          }
        },
        { field: 'address', headerName: 'Address', flex: 1, type: 'string' }
      ];
  
      const [searchData, setSearchData] = React.useState<any>({
        pageSize: 10
      });
    
      //Dialogs and snacks
      const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
          open: false,
          message: "",
          subMessage: "",
          severity: undefined,
          onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

      function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
        setSnackSettings({
          open: true,
          message: message,
          subMessage: subMessage,
          severity: colour,
          onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });
      }

      function OnSelectionChanged(model: GridSelectionModel) {
        const ids = new Set(selectionModel);
        if (model.length > 1) {
          setSelectionModel(model.filter((s) => !ids.has(s)));
          selectedCustomerAddress.current = gridData.find(x => x.id===model.filter((s) => !ids.has(s))[0]);
          setCustomerAddress(selectedCustomerAddress.current?.address ?? "");
        } 
        else {
          setSelectionModel(model);
          selectedCustomerAddress.current = gridData.find(x => x.id===model.filter((s) => !ids.has(s))[0]);
          if (model.length === 0) {
            setShowAddress(false);
            setButtonSelector(1);
            setSaveMode(0);
            setCustomerAddress("");
          } else {
            setShowAddress(true);
            setButtonSelector(2);
            setSaveMode(2);
            setCustomerAddress(selectedCustomerAddress.current?.address ?? "");
          }
        }
      }
  
      function CustomToolbar() {
        return (
          <GridToolbarContainer style={{ display: 'flex' }}>
            <Button style={{ width: "120", minWidth: "100", margin: "2px" }}
              variant="contained" size="small" color="secondary" startIcon={<CheckCircle />} onClick={() => CloseModal(false)}>Select</Button>
            <div style={{ width: "10", minWidth: "10" }}></div>
            {ShowButton()}
            <div style={{ width: `calc(100% - 360)`, minWidth: "10" }}></div>
            <Button style={{ width: "120", minWidth: "100", margin: "2px" }}
              variant="contained" size="small" color="secondary" startIcon={<Cancel />} onClick={(e) => CloseModal(true)}>Cancel</Button>
            <div style={{ width: "10", minWidth: "10" }}></div>
          </GridToolbarContainer>
        );
      }

      const displayAddress = useMemo(()=> DisplayAddress(showAddress, customerAddress), [showAddress, customerAddress]);
      function DisplayAddress(show: boolean, customerAddress: string) {
        if (show)
          return (
            <TextField 
              style={{marginBottom: "5px"}} 
              value={customerAddress}
              label="Customer Address"
              onChange={(e) => setCustomerAddress(e.target.value)}
              multiline
              rows={4}
            />    
          );
        else
          return;
    }

      function ShowButton()
      {
        if (buttonSelector === 1)
          return (
            <Button style={{ width: "120", minWidth: "100", margin: "2px" }}
              variant="contained" size="small" color="secondary" startIcon={<Add />} onClick={() => onAddClicked() }>Add</Button>
          );
        else
          return (
            <Button style={{ width: "120", minWidth: "100", margin: "2px" }}
              variant="contained" size="small" color="secondary" startIcon={<Save />} onClick={() => onSaveClicked() }>Save</Button>
          );
      }

      function onAddClicked()
      {
        setCustomerAddress("");
        setShowAddress(true);
        setButtonSelector(2);
        setSaveMode(1);
      }

      function onSaveClicked()
      {
        setShowAddress(false);
        setButtonSelector(1);
        DoSave();
      }
      
      function LoadData(customerId: number) {
        setLoading(true);
  
        const params = { customerId:  customerId};
        axios.get<CustomerAddressItem[]>('Api/V1/Customer/Address', {
          params,
          headers: {
            'Content-Encoding': 'gzip'
          }
        })
          .then((result) => {
            result.data.forEach(i => {
              i.id = i.id;
            });
            setGridData(result.data)
            setLoading(false);
          },
            (error) => {
              setGridData([]);
              setLoading(false);
              console.log(error);
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          )
          .catch((error) => {
            setLoading(false);
            console.log(error);
            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
          });
      }

      function DoSave() {
        if (saveMode===1)
        {
          let m = {
            customerId: customerId,
            id: 0,
            address: customerAddress
          }
          if (customerAddress!="") {
            axios.post<CustomerAddressItem>('Api/V1/Customer/Address/', m).then(
              (result) => {
                let tempData = [...gridData, result.data];
                setGridData(tempData);
                setSaveMode(0);
              },
              (error) => {
                console.log(error);
                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
              }
            );
          }
        }
        else if (saveMode===2)
        {
          let m = selectedCustomerAddress;
          if (m.current && customerAddress!="")
          {
            m.current.address = customerAddress;
            axios.put<CustomerAddressItem>('Api/V1/Customer/Address/', m.current).then(
              (result) => {
                let tempData = [...gridData];
                tempData.forEach(x => {
                  if(x.id === m.current?.id)
                    x.address = customerAddress;
                })
                setGridData(tempData);
                setSaveMode(0);
              },
              (error) => {
                console.log(error);
                showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
              }
            );
          }
        }
      }

      function DeleteRow(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, values: GridRenderCellParams) {
        let tempGridData = [...gridData];
  
        let index = tempGridData.findIndex(x => x.id === values.id);
        if (index > -1) 
        {
          axios.delete('Api/V1/Customer/Address/', {
            data: tempGridData[index]
          }).then(
            (result) => {
              if (result.data)
              {
                tempGridData.splice(index, 1);
                setGridData(tempGridData);
              }
            },
            (error) => {
              console.log(error);
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          );
        }
      }
  
      useImperativeHandle(ref, () => ({
        Initialise(customerId: number) {
          setOpen(true);
          renewToken();
    
          setCustomerId(customerId);
          setShowAddress(false);
          LoadData(customerId);
          setSelectionModel([]);
          setButtonSelector(1);
        },
      }));
  
      function CloseModal(isCancel: boolean) {
        setOpen(false);
        if (isCancel)
            selectedCustomerAddress.current = undefined;
  
        setSearchData({
          contains: true,
          searchText: "",
          customer: 0,
          region: "ALL",
          pageSize: 10
        });

        props.onClose(!isCancel && selectedCustomerAddress.current, selectedCustomerAddress);
      }
  
      return (
        <div>
          <Modal open={open} onClose={() => CloseModal(true)}>
            <div className={style.ModalMainMedium}>
              {!loading ? (
                <div className={style.ModalBody}
                  style={{minWidth: "250px"}}
                >
                  <Title pageTitle="Customer Address" />
  
                  <div className={style.ModalHeading}>
                    <Typography variant="h5">Customer Address</Typography>
                  </div>
                  {displayAddress}
                  <div className={style.ModalContent}>
                    <div className={style.PageContent} style={{ minHeight: "200px" }}>
                      <DataGridPro
                        getRowId={(row) => row.id}
                        rows={gridData}
                        columns={columns}
                        pageSize={searchData.pageSize}
                        onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
                        pagination
                        rowsPerPageOptions={[10, 30, 50, 100]}
                        loading={loading}
                        checkboxSelection
                        components={{
                          Toolbar: CustomToolbar,
                          Footer: CustomTablePagination
                        }}
                        componentsProps={{
                          footer: { rowsPerPageOptions: [10, 30, 50, 100] }
                        }}
                        selectionModel={selectionModel}
                        onSelectionModelChange={(selectionModel) => {
                          OnSelectionChanged(selectionModel)
                        }}
                        rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <CircularProgress color="primary" />
                </div>
              )}
            </div>
          </Modal>
  
          <SnackBarMessage {...snackSettings} />
  
        </div>
      );
  
  
    }
  
  );
